import { Button } from '@/shared';
import {
  AddHouseForm,
  HouseDetailMenu,
  HousesErrorBoundary,
  ImageGallery,
} from '../components';
import { useHouse } from '../hooks';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ModalWrapper } from '../../../shared/components/ui/ModalWrapper';
import { DeleteHouseModal } from '../components/modals';
import { ResidentTable } from '@/features/residents';
import { useAuth } from '@/app/providers';

const houseImages = [
  'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg',
  'https://images.pexels.com/photos/259962/pexels-photo-259962.jpeg',
  'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg',
  'https://images.pexels.com/photos/2102587/pexels-photo-2102587.jpeg',
];

const HousesDetailsPageContent = () => {
  const { user } = useAuth();
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const { houseId } = useParams({ from: '/houses/$houseId' });
  const { data, isLoading, error } = useHouse(houseId!);
  const house = data?.house;
  const navigate = useNavigate();

  const handleDeleteSuccess = () => {
    // Navigate back to houses list after successful deletion
    navigate({ to: '/houses' });
  };

  if (isLoading) return <p>Loading...</p>;
  if (error) return <p>Error loading house details.</p>;
  if (!house) return <p>House not found.</p>;

  return (
    <div className="min-h-screen bg-background w-full px-0 sm:px-6 lg:px-8 pb-10">
      {/* Header  */}
      <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-4 mb-6">
        <div className="text-center lg:text-start w-full lg:w-[80%]">
          <h3 className="text-xl md:text-3xl font-bold mb-2 text-text-primary">
            House details
          </h3>
          <p className="text-gray-600 mb-0 md:mb-8 text-base md:text-xl text-text-secondary">
            House management / Manage houses / House details
          </p>
        </div>
        <Button
          variant="primary"
          size="lg"
          leftIcon={
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 1V7M7 7V13M7 7H13M7 7L1 7"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          onClick={() => {
            setIsModalOpen(true);
          }}
        >
          Add houses
        </Button>
      </div>
      <div className="">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
          <div className="col-span-1 xl:col-span-3">
            {/* content  */}
            <div>
              <div>
                <ImageGallery
                  images={
                    house!.images.length > 0 ? house!.images : houseImages
                  }
                />
              </div>

              {/* Title section  */}
              <div className="flex items-center justify-between gap-6 mb-6">
                <div className="text-start w-full lg:w-[80%]">
                  <h3 className="text-xl md:text-3xl font-bold mb-2 text-text-primary">
                    {house!.name}
                  </h3>
                  <p className="text-gray-600 text-base md:text-xl text-text-secondary">
                    {house!.address}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {/* Share button  */}
                  <div className="w-12 h-12 cursor-pointer flex items-center justify-center card ">
                    <svg
                      width="18"
                      height="20"
                      viewBox="0 0 18 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 20C14.1667 20 13.4583 19.7083 12.875 19.125C12.2917 18.5417 12 17.8333 12 17C12 16.9 12.025 16.6667 12.075 16.3L5.05 12.2C4.78333 12.45 4.475 12.6458 4.125 12.7875C3.775 12.9292 3.4 13 3 13C2.16667 13 1.45833 12.7083 0.875 12.125C0.291667 11.5417 0 10.8333 0 10C0 9.16667 0.291667 8.45833 0.875 7.875C1.45833 7.29167 2.16667 7 3 7C3.4 7 3.775 7.07083 4.125 7.2125C4.475 7.35417 4.78333 7.55 5.05 7.8L12.075 3.7C12.0417 3.58333 12.0208 3.47083 12.0125 3.3625C12.0042 3.25417 12 3.13333 12 3C12 2.16667 12.2917 1.45833 12.875 0.875C13.4583 0.291667 14.1667 0 15 0C15.8333 0 16.5417 0.291667 17.125 0.875C17.7083 1.45833 18 2.16667 18 3C18 3.83333 17.7083 4.54167 17.125 5.125C16.5417 5.70833 15.8333 6 15 6C14.6 6 14.225 5.92917 13.875 5.7875C13.525 5.64583 13.2167 5.45 12.95 5.2L5.925 9.3C5.95833 9.41667 5.97917 9.52917 5.9875 9.6375C5.99583 9.74583 6 9.86667 6 10C6 10.1333 5.99583 10.2542 5.9875 10.3625C5.97917 10.4708 5.95833 10.5833 5.925 10.7L12.95 14.8C13.2167 14.55 13.525 14.3542 13.875 14.2125C14.225 14.0708 14.6 14 15 14C15.8333 14 16.5417 14.2917 17.125 14.875C17.7083 15.4583 18 16.1667 18 17C18 17.8333 17.7083 18.5417 17.125 19.125C16.5417 19.7083 15.8333 20 15 20ZM15 18C15.2833 18 15.5208 17.9042 15.7125 17.7125C15.9042 17.5208 16 17.2833 16 17C16 16.7167 15.9042 16.4792 15.7125 16.2875C15.5208 16.0958 15.2833 16 15 16C14.7167 16 14.4792 16.0958 14.2875 16.2875C14.0958 16.4792 14 16.7167 14 17C14 17.2833 14.0958 17.5208 14.2875 17.7125C14.4792 17.9042 14.7167 18 15 18ZM3 11C3.28333 11 3.52083 10.9042 3.7125 10.7125C3.90417 10.5208 4 10.2833 4 10C4 9.71667 3.90417 9.47917 3.7125 9.2875C3.52083 9.09583 3.28333 9 3 9C2.71667 9 2.47917 9.09583 2.2875 9.2875C2.09583 9.47917 2 9.71667 2 10C2 10.2833 2.09583 10.5208 2.2875 10.7125C2.47917 10.9042 2.71667 11 3 11ZM15 4C15.2833 4 15.5208 3.90417 15.7125 3.7125C15.9042 3.52083 16 3.28333 16 3C16 2.71667 15.9042 2.47917 15.7125 2.2875C15.5208 2.09583 15.2833 2 15 2C14.7167 2 14.4792 2.09583 14.2875 2.2875C14.0958 2.47917 14 2.71667 14 3C14 3.28333 14.0958 3.52083 14.2875 3.7125C14.4792 3.90417 14.7167 4 15 4Z"
                        fill="#474747"
                      />
                    </svg>
                  </div>

                  {/* Menu button 3 dots  */}
                  <button
                    className="w-12 h-12 cursor-pointer flex items-center justify-center card "
                    onClick={() => setShowMenu((prev) => !prev)}
                  >
                    <svg
                      width="4"
                      height="16"
                      viewBox="0 0 4 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14C0 13.45 0.195833 12.9792 0.5875 12.5875C0.979167 12.1958 1.45 12 2 12C2.55 12 3.02083 12.1958 3.4125 12.5875C3.80417 12.9792 4 13.45 4 14C4 14.55 3.80417 15.0208 3.4125 15.4125C3.02083 15.8042 2.55 16 2 16ZM2 10C1.45 10 0.979167 9.80417 0.5875 9.4125C0.195833 9.02083 0 8.55 0 8C0 7.45 0.195833 6.97917 0.5875 6.5875C0.979167 6.19583 1.45 6 2 6C2.55 6 3.02083 6.19583 3.4125 6.5875C3.80417 6.97917 4 7.45 4 8C4 8.55 3.80417 9.02083 3.4125 9.4125C3.02083 9.80417 2.55 10 2 10ZM2 4C1.45 4 0.979167 3.80417 0.5875 3.4125C0.195833 3.02083 0 2.55 0 2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0C2.55 0 3.02083 0.195833 3.4125 0.5875C3.80417 0.979167 4 1.45 4 2C4 2.55 3.80417 3.02083 3.4125 3.4125C3.02083 3.80417 2.55 4 2 4Z"
                        fill="#474747"
                      />
                    </svg>

                    {/* menu  */}
                    {showMenu && (
                      <HouseDetailMenu
                        id={house!.id}
                        houseName={house!.name}
                        onClose={() => setShowMenu(false)}
                        onDelete={() => {
                          setIsDeleteModalOpen(true);
                        }}
                      />
                    )}
                  </button>
                </div>
              </div>

              {/* description  */}
              <div className="text-xl text-text-secondary mb-4 md:mb-6">
                {house!.description}
              </div>

              {/* details  */}
              <div>
                <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-6 text-text-primary">
                  Details
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
                  {[
                    {
                      id: 1,
                      icon: '/assets/icons/bedrooms.svg',
                      label: 'Bedrooms',
                      value: house!.bedrooms,
                    },
                    {
                      id: 2,
                      icon: '/assets/icons/bathrooms.svg',
                      label: 'Bathrooms',
                      value: house!.bathrooms,
                    },
                    {
                      id: 3,
                      icon: '/assets/icons/area.svg',
                      label: 'Area',
                      value: house!.area,
                    },
                    {
                      id: 4,
                      icon: '/assets/icons/apartment-status.svg',
                      label: 'Apart Status',
                      value: house!.isFurnished ? 'Furnished' : 'Not Furnished',
                    },
                    {
                      id: 5,
                      icon: '/assets/icons/parking.svg',
                      label: 'Parking',
                      value: house!.isParkingAvailable
                        ? 'Available'
                        : 'Unavailable',
                    },
                    {
                      id: 6,
                      icon: '/assets/icons/calender.svg',
                      label: 'Built year',
                      value: house!.builtYear,
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col items-center justify-center rounded-lg shadow-lg mb-4 md:mb-6 p-4"
                    >
                      <div className="w-12 h-12 flex items-center justify-center mb-2 md:mb-4 card">
                        <img src={item.icon} alt={item.label} />
                      </div>

                      <p className="text-base md:text-xl text-text-secondary mb-2 md:mb-4">
                        {item.label}
                      </p>
                      <p className="text-base md:text-xl text-text-primary font-bold">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Resident table  */}
                <div className="w-full">
                  {!house.residents || house.residents.length === 0 ? (
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold mb-6 text-text-primary">
                        Residents
                      </h3>
                      <div className="w-full py-5 text-center">
                        No Residents yet
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <ResidentTable data={house.residents} viewType="home" className='min-h-[50vh]' />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Details  */}
          <div className="col-span-1">
            <h4 className="text-xl text-text-primary font-bold mb-4">
              Contact Details
            </h4>
            <div className="mb-6 flex flex-row xl:flex-col flex-wrap justify-between xl:justify-between">
              {[
                {
                  icon: '/assets/icons/phone.svg',
                  label: 'Contact number',
                  value: house!.phoneNumber || 'Not Available',
                },
                {
                  icon: '/assets/icons/status.svg',
                  label: 'Status',
                  value:
                    house!.status === 'ACTIVE' ? 'Available' : 'Not Available',
                },
                {
                  icon: '/assets/icons/dollar.svg',
                  label: 'Program fees',
                  value: `${house!.currency === 'usd' ? '$' : house!.currency} ${house!.price}`,
                },
                {
                  icon: '/assets/icons/property-type.svg',
                  label: 'Type',
                  value: house!.type || 'Villa',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="w-full sm:w-auto flex items-center gap-3 p-2 mb-4"
                >
                  <img src={item.icon} alt={item.label} />
                  <div className='text-sm md:text-base'>
                    <p className="text-text-secondary mb-1">{item.label}</p>
                    <p className="text-base md:text-xl text-text-primary">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {user?.role === 'OWNER' && (
              <div className="mb-6">
                <h4 className="text-xl text-text-primary font-bold mb-4">
                  Assigned house manager
                </h4>
                {house!.manager ? (
                  <div className="flex items-center gap-3 p-2 mb-4">
                    <div className="w-12 h-12">
                      <img
                        src="https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg"
                        alt=""
                        className="w-full h-full rounded-full"
                      />
                    </div>
                    <div>
                      <p className="text-xl text-text-primary">
                        {house?.manager?.name}
                      </p>
                      <p className="text-text-secondary mb-1">Asst. Manager</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="w-full py-2 text-center">
                      No Manager yest
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Home Modal  */}
      <ModalWrapper isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="fixed inset-0 bg-[#00000063] bg-opacity-50 flex items-center justify-center z-50">
          <AddHouseForm
            onClose={() => {
              setIsModalOpen(false);
            }}
          />
        </div>
      </ModalWrapper>

      {/* Delete House Modal */}
      <DeleteHouseModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteSuccess}
        house={{ id: house!.id, name: house!.name }}
      />
    </div>
  );
};

export const HouseDetailsPage = () => {
  return (
    <HousesErrorBoundary onRetry={() => window.location.reload()}>
      <HousesDetailsPageContent />
    </HousesErrorBoundary>
  );
};
